import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import type { Tab } from '../../../hooks/multiWindow/useTabManager'
import { useAutoScrollbar } from '../../../hooks/useAutoScrollbar'
import { PersonalSidebarContent } from './PersonalSidebarContent'
import { WorkSidebarContent } from './WorkSidebarContent'
import { HomeSidebarContent } from './HomeSidebarContent'
import { AllSpacesSidebarContent } from './AllSpacesSidebarContent'
import { TooltipProvider } from '../../ui/tooltip'
import { SidebarDataProvider } from './SidebarDataContext'
import { DiscoverCard } from '../DiscoverCard'
import './StandardSidebar.scss'
import type { TabIcon } from '../../../hooks/multiWindow'

export interface StandardSidebarProps {
  lifeSpace: string
  activeTab?: Tab
  tabs?: Tab[]
  openTab: (tab: Tab) => void
  navigateActiveTab: (updates: { path: string; title: string; icon?: TabIcon }) => void
  onCloseTabByPath?: (path: string) => void
  onUnpinByPath?: (path: string) => void
  onPinByPath?: (path: string) => void
  openPreviewTab?: (tab: Omit<Tab, 'id'> & { id?: string }) => void
  promotePreviewBySpace?: (lifeSpace: string) => void
}

const StandardSidebarComponent: React.FC<StandardSidebarProps> = ({
  lifeSpace,
  activeTab,
  tabs = [],
  openTab,
  navigateActiveTab,
  onCloseTabByPath,
  onUnpinByPath,
  onPinByPath,
  openPreviewTab,
  promotePreviewBySpace
}) => {

  // Memoize sidebar content based on lifeSpace and activeTab
  const sidebarContent = useMemo(() => {
    switch (lifeSpace) {
      case 'personal':
        return <PersonalSidebarContent activeTab={activeTab} openTab={openTab} navigateActiveTab={navigateActiveTab} tabs={tabs} onCloseTabByPath={onCloseTabByPath} onUnpinByPath={onUnpinByPath} onPinByPath={onPinByPath} openPreviewTab={openPreviewTab} promotePreviewBySpace={promotePreviewBySpace} />
      case 'work':
        return <WorkSidebarContent activeTab={activeTab} openTab={openTab} navigateActiveTab={navigateActiveTab} tabs={tabs} onCloseTabByPath={onCloseTabByPath} onUnpinByPath={onUnpinByPath} onPinByPath={onPinByPath} openPreviewTab={openPreviewTab} promotePreviewBySpace={promotePreviewBySpace} />
      case 'home':
        return <HomeSidebarContent activeTab={activeTab} openTab={openTab} navigateActiveTab={navigateActiveTab} tabs={tabs} onCloseTabByPath={onCloseTabByPath} onUnpinByPath={onUnpinByPath} onPinByPath={onPinByPath} openPreviewTab={openPreviewTab} promotePreviewBySpace={promotePreviewBySpace} />
      case 'all':
        return <AllSpacesSidebarContent activeTab={activeTab} openTab={openTab} navigateActiveTab={navigateActiveTab} tabs={tabs} onCloseTabByPath={onCloseTabByPath} onUnpinByPath={onUnpinByPath} onPinByPath={onPinByPath} openPreviewTab={openPreviewTab} promotePreviewBySpace={promotePreviewBySpace} />
      default:
        // Custom Life Space - dynamic content
        return (
          <div className="standard-sidebar__custom-content">
            <p>Custom Life Space: {lifeSpace}</p>
          </div>
        )
    }
  }, [lifeSpace, activeTab, openTab, navigateActiveTab, tabs, onCloseTabByPath, onUnpinByPath, onPinByPath, openPreviewTab, promotePreviewBySpace])

  // Auto-hiding scrollbar with fade indicators. `lifeSpace` als Schlüssel: Der Container ist beim
  // Wechsel ein neues Element, die Beobachter müssen mit (#285).
  const { containerRef, scrollbarVisible, showTopFade, showBottomFade, onScroll } = useAutoScrollbar(lifeSpace)

  /*
    **Scroll-Stand je Lebensbereich, solange die App läuft** (#285, CONTEXT.md → Sidebar). Der
    Container ist nach jedem Wechsel ein neues Element und stünde oben; gemerkt wird im Bildlauf
    (der alte Container ist beim Wechsel schon ausgehängt, ein Aufräumer käme zu spät).
    Wiederhergestellt vor dem Malen — die Daten stehen dank `SidebarDataProvider` schon da.
  */
  const scrollPositions = useRef<Record<string, number>>({})
  const handleScroll = useCallback(() => {
    onScroll()
    const el = containerRef.current
    if (el) scrollPositions.current[lifeSpace] = el.scrollTop
  }, [onScroll, containerRef, lifeSpace])

  // Der zuletzt eingehängte Lebensbereich: Nur ein echter Wechsel stellt her — die zweite Runde der
  // Effekte im StrictMode (Einhängen, Aufräumen, Einhängen) fände sonst einen „besuchten“ Stand vor.
  const mountedSpaceRef = useRef<string | null>(null)
  const restoredRef = useRef(false)
  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el || mountedSpaceRef.current === lifeSpace) return
    mountedSpaceRef.current = lifeSpace
    const saved = scrollPositions.current[lifeSpace]
    if (saved === undefined) {
      // Erster Besuch: oben, und der Sprung zum aktiven Eintrag gilt wie bisher.
      scrollPositions.current[lifeSpace] = el.scrollTop
      return
    }
    el.scrollTop = saved
    restoredRef.current = true
  }, [lifeSpace, containerRef])

  // Scroll to active item if not visible (with 40px offset for fade gradient).
  // When the item has a sub-menu, scroll so the project sits near the top
  // and as many sub-items as possible are visible below it.
  useEffect(() => {
    // Beim Wechsel in einen besuchten Lebensbereich gewinnt der gemerkte Stand (#285).
    if (restoredRef.current) return
    const path = activeTab?.path
    if (!path) return
    const container = containerRef.current
    if (!container) return
    // Delay one frame so the sub-menu has time to render after auto-expand.
    const rafId = requestAnimationFrame(() => {
      const item = container.querySelector(`[data-path="${CSS.escape(path)}"]`) as HTMLElement | null
      if (!item) return
      // Check if the next sibling is a sub-menu (rendered by ProjectSidebarItem)
      const subMenu = item.nextElementSibling?.classList.contains('sidebar-sub-menu')
        ? item.nextElementSibling as HTMLElement
        : null
      const cRect = container.getBoundingClientRect()
      const iRect = item.getBoundingClientRect()
      const fadeOffset = 40
      if (subMenu) {
        const smRect = subMenu.getBoundingClientRect()
        // Total height: from item top to sub-menu bottom
        const totalHeight = smRect.bottom - iRect.top
        const viewportHeight = cRect.height - fadeOffset * 2
        if (totalHeight <= viewportHeight) {
          // Everything fits: scroll so the full block is visible
          if (iRect.top < cRect.top + fadeOffset) {
            container.scrollTo({ top: container.scrollTop + (iRect.top - cRect.top) - fadeOffset, behavior: 'smooth' })
          } else if (smRect.bottom > cRect.bottom - fadeOffset) {
            container.scrollTo({ top: container.scrollTop + (smRect.bottom - cRect.bottom) + fadeOffset, behavior: 'smooth' })
          }
        } else {
          // Too tall: pin project at the top so sub-items fill remaining space
          container.scrollTo({ top: container.scrollTop + (iRect.top - cRect.top) - fadeOffset, behavior: 'smooth' })
        }
      } else {
        // No sub-menu: simple scroll into view
        if (iRect.top < cRect.top + fadeOffset) {
          container.scrollTo({ top: container.scrollTop + (iRect.top - cRect.top) - fadeOffset, behavior: 'smooth' })
        } else if (iRect.bottom > cRect.bottom - fadeOffset) {
          container.scrollTo({ top: container.scrollTop + (iRect.bottom - cRect.bottom) + fadeOffset, behavior: 'smooth' })
        }
      }
    })
    return () => cancelAnimationFrame(rafId)
  }, [activeTab?.path, containerRef])

  // Nach dem Sprung-Effekt (Reihenfolge der Deklaration), in jedem Commit: Das Merkzeichen gilt nur
  // für den Commit des Wechsels. Bliebe es stehen, verschluckte es den nächsten normalen Sprung.
  useEffect(() => {
    restoredRef.current = false
  })

  return (
    <TooltipProvider>
      <SidebarDataProvider>
      <div className="standard-sidebar">
        <div className="standard-sidebar__scroll-wrapper">
          <div
            key={lifeSpace}
            ref={containerRef}
            data-space={lifeSpace}
            className={`standard-sidebar__content${scrollbarVisible ? ' standard-sidebar__content--scroll-visible' : ''}`}
            onScroll={handleScroll}
          >
            {sidebarContent}
          </div>
          {showTopFade && (
            <div className="auto-scrollbar__fade auto-scrollbar__fade--top">
              <svg className="auto-scrollbar__fade-icon" width="16" height="16" viewBox="0 0 16 16">
                <polyline fill="none" stroke="currentColor" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" points="15.5,10.5 8,5.5 0.5,10.5" />
              </svg>
            </div>
          )}
          {showBottomFade && (
            <div className="auto-scrollbar__fade auto-scrollbar__fade--bottom">
              <svg className="auto-scrollbar__fade-icon" width="16" height="16" viewBox="0 0 16 16">
                <polyline fill="none" stroke="currentColor" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" points="15.5,5.5 8,10.5 0.5,5.5" />
              </svg>
            </div>
          )}
        </div>
        <DiscoverCard lifeSpace={lifeSpace} navigateActiveTab={navigateActiveTab} />
      </div>
      </SidebarDataProvider>
      </TooltipProvider>
  )
}

// Memoize StandardSidebar to prevent unnecessary re-renders
// Only re-renders when props change
const StandardSidebar = React.memo(StandardSidebarComponent)

export default StandardSidebar